/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './MenuItemList';

const MenuItemListManager: ComponentManagerFn<'MenuItemList'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MenuItemList'>(platform, 'MenuItemList'),
    type: 'MenuItemList',
    name: 'Menu Item List',
    isChildAllowed: (page, item, childType) =>
      (['MenuItem'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Datastore Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      noDataFoundMessage: {
        label: 'No Data Found Message',
        type: 'string',
      },
    },
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    category: 'Toolbar',
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Menu Item List is a data-driven container for menus. Unlike standard MenuItem components which are added manually, this component connects to a Data Store to generate a dynamic list of menu options based on database records.
Use this component when the available actions or options need to change based on data, such as a "Select Project" menu that lists all active projects assigned to a user.

**1. Data Connection**<br/>
* **Datastore Alias:** Select a Data Store (CloudDataStore, LocalDataStore, etc.) to source the list items. The component will iterate through the records to create the menu rows.

**2. Structure**<br/>
* **Allowed Child:** It accepts MenuItem as a child. This child likely acts as the template for each row. You would bind the properties of this child MenuItem (like label and icon) to the fields of the Data Store alias to display the correct data for each record.

**3. Feedback**<br/>
* **No Data Found Message:** A text string displayed in the menu if the connected Data Store returns zero records (e.g., "No Projects Available"). This prevents the user from seeing a confusingly empty or broken menu.

**4. Best Use Cases**<br/>
* **Dynamic Navigation:** A "Jump to..." menu listing all customers.
* **Recent Items:** A "Recent Files" menu populated by a local data store.
* **Category Filters:** A dropdown listing all product categories defined in the database.`,
  };
};

export default MenuItemListManager;
